// ==================== FILE: src/utils/authStorage.js ====================
// Tenant-namespaced customer auth storage.
//
// This app is a single SPA bundle deployed per tenant subdomain, but in some
// contexts (local/dev testing via X-Tenant header switching, shared hosts,
// proxy setups) the same browser storage can end up read under more than one
// tenant. Rather than store auth under a fixed cookie name and validate it
// against the current tenant after the fact, the tenant id is baked directly
// into the cookie name — a lookup for tenant 50 can then never even see a
// cookie written for tenant 12, by construction, with no separate check to
// forget. Same reasoning already applied to the cached store details in
// App.js (see the tenant_id check around 'selected_store_details').
// =========================================================================

import Cookies from 'js-cookie';
import { getTenantId } from '../config/tenantConfig';

const BASE_AUTH_COOKIE = 'resto_auth_token';
const BASE_USER_ID_COOKIE = 'resto_user_id';
const BASE_USER_PHONE_COOKIE = 'resto_user_phone';

// Cookie names used before this file existed — flat, not tenant-namespaced.
// Cleared opportunistically so a leftover one can never be mistaken for the
// current tenant's session.
const LEGACY_COOKIE_NAMES = [BASE_AUTH_COOKIE, BASE_USER_ID_COOKIE, BASE_USER_PHONE_COOKIE];

const tenantCookieName = (base, tenantId) => `${base}_${tenantId}`;

const cookieOptions = () => ({
  expires: 30,
  path: '/',
  secure: window.location.protocol === 'https:',
  sameSite: 'Lax'
});

export function clearAuthSession() {
  const tenantId = getTenantId();
  if (tenantId) {
    Cookies.remove(tenantCookieName(BASE_AUTH_COOKIE, tenantId), { path: '/' });
    Cookies.remove(tenantCookieName(BASE_USER_ID_COOKIE, tenantId), { path: '/' });
    Cookies.remove(tenantCookieName(BASE_USER_PHONE_COOKIE, tenantId), { path: '/' });
  }
  LEGACY_COOKIE_NAMES.forEach(name => Cookies.remove(name, { path: '/' }));
}

// Persist the session under a cookie name scoped to the current tenant.
export function setAuthSession(token, userId, phone) {
  const tenantId = getTenantId();
  if (!tenantId) return; // tenant not resolved yet — nowhere safe to store this
  const opts = cookieOptions();
  Cookies.set(tenantCookieName(BASE_AUTH_COOKIE, tenantId), token, opts);
  Cookies.set(tenantCookieName(BASE_USER_ID_COOKIE, tenantId), String(userId), opts);
  if (phone) Cookies.set(tenantCookieName(BASE_USER_PHONE_COOKIE, tenantId), phone, opts);
}

// Returns { token, userId, phone } for the CURRENT tenant only, or null.
export function getValidAuthSession() {
  const tenantId = getTenantId();
  if (!tenantId) return null;

  const token = Cookies.get(tenantCookieName(BASE_AUTH_COOKIE, tenantId));
  const userId = Cookies.get(tenantCookieName(BASE_USER_ID_COOKIE, tenantId));
  const phone = Cookies.get(tenantCookieName(BASE_USER_PHONE_COOKIE, tenantId));

  if (!token || !userId) return null;
  return { token, userId, phone: phone || '' };
}
